/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/index";
import type {
  CampaignTargeting,
  CustomerCondition,
  ConditionGroup,
} from "@lemmonhq/types";
import { CustomerPicker } from "./CustomerPicker";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CONDITION_LABELS: Record<CustomerCondition["type"], string> = {
  total_spending: "Total Spending",
  last_order_days_ago: "Last Order",
  birthday: "Birthday",
  ordered_category: "Ordered Category",
  status_level: "Status Level",
  order_count: "Order Count",
};

const PERIOD_OPTIONS = [
  { value: "30d", label: "last 30 days" },
  { value: "90d", label: "last 90 days" },
  { value: "365d", label: "last year" },
  { value: "all_time", label: "all time" },
];

function defaultCondition(type: CustomerCondition["type"]): CustomerCondition {
  switch (type) {
    case "total_spending":
      return { type: "total_spending", operator: "gte", value: 5000, period: "90d" };
    case "last_order_days_ago":
      return { type: "last_order_days_ago", operator: "gte", value: 30 };
    case "birthday":
      return { type: "birthday", window: "this_month" };
    case "ordered_category":
      return { type: "ordered_category", category_ids: [], period: "90d" };
    case "status_level":
      return { type: "status_level", value: "" };
    case "order_count":
      return { type: "order_count", operator: "gte", value: 3, period: "all_time" };
  }
}

// ─── ConditionRow ─────────────────────────────────────────────────────────────

const ConditionRow = defineComponent<{
  condition: CustomerCondition;
  onChange: (c: CustomerCondition) => void;
  onRemove: () => void;
}>({
  name: "ConditionRow",
  props: ["condition", "onChange", "onRemove"],
  setup(props) {
    const update = (patch: Partial<CustomerCondition>) =>
      props.onChange({ ...props.condition, ...patch } as CustomerCondition);

    const renderFields = () => {
      const c = props.condition;
      switch (c.type) {
        case "total_spending":
          return (
            <div class="flex items-center gap-2 flex-wrap">
              <select
                value={c.operator}
                onChange={(e) => update({ operator: (e.target as HTMLSelectElement).value as any })}
                class="select select-bordered select-sm"
              >
                <option value="gte">at least</option>
                <option value="lte">at most</option>
              </select>
              <span class="text-base-content/50 text-sm">$</span>
              <input
                type="number"
                value={c.value / 100}
                onInput={(e) => update({ value: Math.round(parseFloat((e.target as HTMLInputElement).value) * 100) })}
                class="input input-bordered input-sm w-24"
                min={0}
                step={0.01}
              />
              <span class="text-base-content/50 text-sm">in</span>
              <select
                value={c.period}
                onChange={(e) => update({ period: (e.target as HTMLSelectElement).value as any })}
                class="select select-bordered select-sm"
              >
                {PERIOD_OPTIONS.map((p) => <option value={p.value}>{p.label}</option>)}
              </select>
            </div>
          );

        case "last_order_days_ago":
          return (
            <div class="flex items-center gap-2 flex-wrap">
              <select
                value={c.operator}
                onChange={(e) => update({ operator: (e.target as HTMLSelectElement).value as any })}
                class="select select-bordered select-sm"
              >
                <option value="gte">more than</option>
                <option value="lte">less than</option>
              </select>
              <input
                type="number"
                value={c.value}
                onInput={(e) => update({ value: parseInt((e.target as HTMLInputElement).value) || 1 })}
                class="input input-bordered input-sm w-20"
                min={1}
              />
              <span class="text-base-content/50 text-sm">days ago</span>
            </div>
          );

        case "birthday":
          return (
            <select
              value={c.window}
              onChange={(e) => update({ window: (e.target as HTMLSelectElement).value as any })}
              class="select select-bordered select-sm"
            >
              <option value="today">is today</option>
              <option value="this_week">is this week</option>
              <option value="this_month">is this month</option>
            </select>
          );

        case "ordered_category":
          return (
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-base-content/50 text-sm">category IDs</span>
              <input
                type="text"
                value={c.category_ids.join(", ")}
                onInput={(e) => {
                  const ids = (e.target as HTMLInputElement).value
                    .split(/[,\s]+/)
                    .map((s) => parseInt(s.trim()))
                    .filter((n) => !isNaN(n) && n > 0);
                  update({ category_ids: ids });
                }}
                class="input input-bordered input-sm w-36"
                placeholder="e.g. 1, 2, 5"
              />
              <span class="text-base-content/50 text-sm">in</span>
              <select
                value={c.period}
                onChange={(e) => update({ period: (e.target as HTMLSelectElement).value as any })}
                class="select select-bordered select-sm"
              >
                {PERIOD_OPTIONS.map((p) => <option value={p.value}>{p.label}</option>)}
              </select>
            </div>
          );

        case "status_level":
          return (
            <input
              type="text"
              value={c.value}
              onInput={(e) => update({ value: (e.target as HTMLInputElement).value })}
              class="input input-bordered input-sm w-32"
              placeholder="e.g. gold"
            />
          );

        case "order_count":
          return (
            <div class="flex items-center gap-2 flex-wrap">
              <select
                value={c.operator}
                onChange={(e) => update({ operator: (e.target as HTMLSelectElement).value as any })}
                class="select select-bordered select-sm"
              >
                <option value="gte">at least</option>
                <option value="lte">at most</option>
              </select>
              <input
                type="number"
                value={c.value}
                onInput={(e) => update({ value: parseInt((e.target as HTMLInputElement).value) || 0 })}
                class="input input-bordered input-sm w-20"
                min={0}
              />
              <span class="text-base-content/50 text-sm">orders in</span>
              <select
                value={c.period}
                onChange={(e) => update({ period: (e.target as HTMLSelectElement).value as any })}
                class="select select-bordered select-sm"
              >
                {PERIOD_OPTIONS.map((p) => <option value={p.value}>{p.label}</option>)}
              </select>
            </div>
          );
      }
    };

    return () => (
      <div class="flex items-start gap-2 bg-base-300/30 rounded-lg p-2.5">
        <select
          value={props.condition.type}
          onChange={(e) => {
            const newType = (e.target as HTMLSelectElement).value as CustomerCondition["type"];
            props.onChange(defaultCondition(newType));
          }}
          class="select select-bordered select-sm min-w-40"
        >
          {(Object.keys(CONDITION_LABELS) as CustomerCondition["type"][]).map((type) => (
            <option value={type}>{CONDITION_LABELS[type]}</option>
          ))}
        </select>
        <div class="flex-1 flex items-center gap-2">{renderFields()}</div>
        <button
          type="button"
          onClick={props.onRemove}
          class="btn btn-ghost btn-xs text-error hover:bg-error/10 mt-0.5"
          title="Remove condition"
        >
          <span class="material-symbols-outlined text-base">close</span>
        </button>
      </div>
    );
  },
});

// ─── TargetingEditor ──────────────────────────────────────────────────────────

export const TargetingEditor = defineComponent<{
  value: CampaignTargeting;
  onChange: (v: CampaignTargeting) => void;
}>({
  name: "TargetingEditor",
  props: ["value", "onChange"],
  setup(props) {
    const trpc = useTrpc();

    // Debounced targeting for audience estimate
    const debouncedTargeting = ref<CampaignTargeting>(props.value);
    let debounceTimer: ReturnType<typeof setTimeout>;
    watch(
      () => props.value,
      (v) => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => { debouncedTargeting.value = v; }, 700);
      },
      { deep: true },
    );

    const estimateQuery = useQuery({
      queryKey: computed(() => ["campaigns", "estimate", JSON.stringify(debouncedTargeting.value)]),
      queryFn: () => trpc.campaigns.estimateAudience.query({ targeting: debouncedTargeting.value }),
      staleTime: 30_000,
    });

    const setMode = (mode: CampaignTargeting["type"]) => {
      if (mode === "explicit") {
        props.onChange({ type: "explicit", customer_ids: [] });
      } else {
        props.onChange({
          type: "conditions",
          groups: [{ conditions: [defaultCondition("last_order_days_ago")] }],
        });
      }
    };

    const updateGroup = (groupIdx: number, group: ConditionGroup) => {
      if (props.value.type !== "conditions") return;
      const groups = [...props.value.groups];
      groups[groupIdx] = group;
      props.onChange({ ...props.value, groups });
    };

    const addGroup = () => {
      if (props.value.type !== "conditions") return;
      props.onChange({
        ...props.value,
        groups: [...props.value.groups, { conditions: [defaultCondition("status_level")] }],
      });
    };

    const removeGroup = (groupIdx: number) => {
      if (props.value.type !== "conditions") return;
      const groups = props.value.groups.filter((_, i) => i !== groupIdx);
      props.onChange({ ...props.value, groups: groups.length ? groups : [{ conditions: [defaultCondition("status_level")] }] });
    };

    const addCondition = (groupIdx: number) => {
      if (props.value.type !== "conditions") return;
      const group = props.value.groups[groupIdx];
      updateGroup(groupIdx, {
        conditions: [...group.conditions, defaultCondition("order_count")],
      });
    };

    const updateCondition = (groupIdx: number, condIdx: number, c: CustomerCondition) => {
      if (props.value.type !== "conditions") return;
      const group = props.value.groups[groupIdx];
      const conditions = [...group.conditions];
      conditions[condIdx] = c;
      updateGroup(groupIdx, { conditions });
    };

    const removeCondition = (groupIdx: number, condIdx: number) => {
      if (props.value.type !== "conditions") return;
      const group = props.value.groups[groupIdx];
      if (group.conditions.length === 1) {
        removeGroup(groupIdx);
        return;
      }
      const conditions = group.conditions.filter((_, i) => i !== condIdx);
      updateGroup(groupIdx, { conditions });
    };

    return () => (
      <div class="space-y-4">
        {/* Mode toggle */}
        <div class="flex gap-2">
          <button
            type="button"
            onClick={() => setMode("conditions")}
            class={`btn btn-sm flex-1 ${props.value.type === "conditions" ? "btn-primary" : "btn-ghost border border-base-content/20"}`}
          >
            <span class="material-symbols-outlined text-base">filter_list</span>
            Conditions
          </button>
          <button
            type="button"
            onClick={() => setMode("explicit")}
            class={`btn btn-sm flex-1 ${props.value.type === "explicit" ? "btn-primary" : "btn-ghost border border-base-content/20"}`}
          >
            <span class="material-symbols-outlined text-base">list</span>
            Explicit List
          </button>
        </div>

        {/* Explicit mode */}
        {props.value.type === "explicit" && (
          <CustomerPicker
            {...{
              selectedIds: props.value.customer_ids,
              onChange: (ids: string[]) =>
                props.onChange({ type: "explicit", customer_ids: ids }),
            }}
          />
        )}

        {/* Conditions mode */}
        {props.value.type === "conditions" && (
          <div class="space-y-3">
            {props.value.groups.map((group, gIdx) => (
              <div key={gIdx}>
                {gIdx > 0 && (
                  <div class="flex items-center gap-3 my-2">
                    <div class="flex-1 h-px bg-base-content/10" />
                    <span class="text-xs font-bold text-base-content/40 uppercase tracking-widest">OR</span>
                    <div class="flex-1 h-px bg-base-content/10" />
                  </div>
                )}
                <div class="border border-base-content/10 rounded-xl p-3 space-y-2 bg-base-200/30">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-semibold text-base-content/40 uppercase tracking-wider">Group {gIdx + 1}</span>
                    {props.value.type === "conditions" && props.value.groups.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeGroup(gIdx)}
                        class="btn btn-ghost btn-xs text-error hover:bg-error/10"
                      >
                        Remove Group
                      </button>
                    )}
                  </div>

                  {group.conditions.map((cond, cIdx) => (
                    <div key={cIdx}>
                      {cIdx > 0 && (
                        <div class="pl-2 py-1">
                          <span class="text-xs font-bold text-primary/60 uppercase tracking-widest">AND</span>
                        </div>
                      )}
                      <ConditionRow
                        {...{
                          condition: cond,
                          onChange: (c: CustomerCondition) => updateCondition(gIdx, cIdx, c),
                          onRemove: () => removeCondition(gIdx, cIdx),
                        }}
                      />
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => addCondition(gIdx)}
                    class="btn btn-ghost btn-xs text-primary w-full mt-1"
                  >
                    <span class="material-symbols-outlined text-sm">add</span>
                    Add Condition
                  </button>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addGroup}
              class="btn btn-outline btn-sm w-full border-dashed border-base-content/20 hover:border-primary"
            >
              <span class="material-symbols-outlined text-base">add_circle</span>
              Add OR Group
            </button>
          </div>
        )}

        {/* Live estimate */}
        <div class={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${estimateQuery.isError.value ? "bg-error/10 text-error" : "bg-base-200/60 text-base-content/70"}`}>
          <span class="material-symbols-outlined text-base">people</span>
          {estimateQuery.isLoading.value ? (
            <span class="flex items-center gap-1.5">
              <span class="loading loading-spinner loading-xs" />
              Estimating...
            </span>
          ) : estimateQuery.isError.value ? (
            <span>Could not estimate audience</span>
          ) : (
            <span>
              <span class="font-semibold text-accent">{estimateQuery.data?.value?.count ?? 0}</span> customers match
            </span>
          )}
        </div>
      </div>
    );
  },
});
